import React, { useState, useEffect } from 'react'
import { useAuth } from '../contexts/AuthContext'
import api from '../services/api'
import { toast } from 'sonner'
import {
  User,
  Rocket,
  Users,
  Monitor,
  FloppyDisk,
  SignOut,
  Key,
  Lightning,
  Trash,
  Microphone,
  Camera,
} from '@phosphor-icons/react'
import { useNavigate, useSearchParams, Link } from 'react-router-dom'
import { useBilling, creditsAsResults, creditTone, joinResults } from '../contexts/BillingContext'

const BYOK_PROVIDERS = [
  { id: 'anthropic', label: 'Anthropic (Claude)', placeholder: 'sk-ant-...' },
  { id: 'openai', label: 'OpenAI (GPT-4o)', placeholder: 'sk-...' },
  { id: 'deepseek', label: 'DeepSeek', placeholder: 'sk-...' },
  { id: 'groq', label: 'Groq', placeholder: 'gsk_...' },
  { id: 'gemini', label: 'Google Gemini', placeholder: 'AIza...' },
]

const CREATOR_TYPES = [
  { id: 'emprendedor', label: 'Emprendedor', emoji: '🚀' },
  { id: 'educador', label: 'Educador', emoji: '📚' },
  { id: 'lifestyle', label: 'Lifestyle', emoji: '🌿' },
  { id: 'gaming', label: 'Gaming', emoji: '🎮' },
  { id: 'tecnologia', label: 'Tecnología', emoji: '💻' },
  { id: 'arte', label: 'Arte', emoji: '🎨' },
  { id: 'fitness', label: 'Fitness', emoji: '💪' },
  { id: 'otro', label: 'Otro', emoji: '✨' },
]

const TONES = [
  { id: 'cercano', label: 'Cercano y humano' },
  { id: 'profesional', label: 'Profesional' },
  { id: 'directo', label: 'Directo' },
  { id: 'inspiracional', label: 'Inspiracional' },
  { id: 'humor', label: 'Con humor' },
  { id: 'educativo', label: 'Educativo' },
]

const PLATFORMS = [
  { id: 'YouTube', label: 'YouTube', emoji: '▶️' },
  { id: 'TikTok', label: 'TikTok', emoji: '🎵' },
  { id: 'Instagram', label: 'Instagram', emoji: '📸' },
  { id: 'LinkedIn', label: 'LinkedIn', emoji: '💼' },
]

const TABS = [
  { id: 'cuenta', label: 'Cuenta', icon: User },
  { id: 'identidad', label: 'Identidad', icon: Rocket },
  { id: 'audiencia', label: 'Audiencia', icon: Users },
  { id: 'voz', label: 'Voz', icon: Microphone },
  { id: 'plataformas', label: 'Plataformas', icon: Monitor },
  { id: 'produccion', label: 'Producción', icon: Camera },
  { id: 'apikey', label: 'API Key', icon: Key },
]

const EQUIPMENT_LEVELS = [
  {
    id: 'mobile',
    label: 'Móvil',
    desc: 'Smartphone — planos básicos y luz natural',
  },
  {
    id: 'basic',
    label: 'Básico',
    desc: 'Cámara mirrorless / DSLR de entrada',
  },
  {
    id: 'prosumer',
    label: 'Prosumer',
    desc: 'Ópticas intercambiables + control de luz',
  },
  {
    id: 'professional',
    label: 'Profesional',
    desc: 'Equipo completo: luces, grips, lentes cinema',
  },
]

function Field({ label, hint, children }) {
  return (
    <div className="space-y-1.5">
      <label className="ds-label mb-0">
        {label}
        {hint && <span className="text-ds-faint font-normal ml-1">— {hint}</span>}
      </label>
      {children}
    </div>
  )
}

function Input({ value, onChange, placeholder, type = 'text' }) {
  return (
    <input
      type={type}
      value={value || ''}
      onChange={e => onChange(e.target.value)}
      placeholder={placeholder}
      className="ds-input"
    />
  )
}

function Textarea({ value, onChange, placeholder, rows = 3, mono = false }) {
  return (
    <textarea
      value={value || ''}
      onChange={e => onChange(e.target.value)}
      placeholder={placeholder}
      rows={rows}
      className={`ds-input resize-none${mono ? ' font-mono text-xs leading-relaxed' : ''}`}
    />
  )
}

function CreditsBar({ purchased, subscription, total, hasbyok }) {
  const { creditWeights } = useBilling()
  if (hasbyok) {
    return (
      <div
        className="flex items-center gap-2 text-xs text-[#30D158]"
        style={{ fontFamily: 'Barlow, sans-serif' }}
      >
        <Lightning size={13} weight="fill" />
        Generaciones ilimitadas con tu API key
      </div>
    )
  }
  const color = creditTone(total)
  // Un crédito no le dice nada a nadie; un guion sí.
  const results = creditsAsResults(total, creditWeights)
  return (
    <div className="space-y-1.5">
      <div
        className="flex justify-between items-center text-xs"
        style={{ fontFamily: 'Barlow, sans-serif' }}
      >
        <span className="text-ds-muted">Créditos disponibles</span>
        <div className="flex items-center gap-1.5">
          <span style={{ color }} className="font-medium">
            {total}
          </span>
          <Link
            to="/pricing"
            title="Comprar créditos"
            className="w-4 h-4 flex items-center justify-center bg-ds-elevated hover:bg-[#282828] text-ds-muted hover:text-ds-text transition-colors text-xs leading-none no-underline"
            style={{ borderRadius: 2 }}
          >
            +
          </Link>
        </div>
      </div>
      {purchased > 0 && (
        <p className="text-ds-muted text-xs" style={{ fontFamily: 'Barlow, sans-serif' }}>
          {subscription} del plan · {purchased} comprados
        </p>
      )}
      {total > 0 && results.length > 0 && (
        <p className="text-ds-muted text-xs" style={{ fontFamily: 'Barlow, sans-serif' }}>
          Da para {joinResults(results)}.
        </p>
      )}
    </div>
  )
}

/* ── Tab content components ─────────────────────────────────────────── */

function TabCuenta({ user, onLogout, settings }) {
  const initials =
    user?.name
      ?.split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2) || '??'
  return (
    <div className="space-y-6">
      <div>
        <h2 className="type-h3 text-ds-text text-base mb-1">Cuenta</h2>
        <p className="text-xs text-ds-muted" style={{ fontFamily: 'Barlow, sans-serif' }}>
          Tu información de acceso
        </p>
      </div>

      <div className="flex items-center gap-4 p-4 card-elevated">
        <div
          className="w-12 h-12 bg-ds-red flex items-center justify-center text-white text-base font-bold flex-shrink-0 type-label"
          style={{ borderRadius: 2 }}
        >
          {initials}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-ds-text font-medium" style={{ fontFamily: 'Barlow, sans-serif' }}>
            {user?.name}
          </p>
          <p className="text-ds-muted text-sm" style={{ fontFamily: 'Barlow, sans-serif' }}>
            {user?.email}
          </p>
        </div>
      </div>

      {settings && (
        <div className="p-4 card-elevated">
          <CreditsBar
            purchased={settings.credits_purchased ?? 0}
            subscription={settings.credits_subscription ?? 0}
            total={settings.credits_total ?? 0}
            hasbyok={settings.has_byok_key}
          />
        </div>
      )}

      <button
        onClick={onLogout}
        className="flex items-center gap-2 text-sm text-ds-muted hover:text-ds-red transition-colors"
        style={{ fontFamily: 'Barlow, sans-serif' }}
      >
        <SignOut size={15} />
        Cerrar sesión
      </button>
    </div>
  )
}

function TabIdentidad({ form, onChange }) {
  return (
    <div className="space-y-5">
      <div>
        <h2 className="type-h3 text-ds-text text-base mb-1">Identidad creativa</h2>
        <p className="text-xs text-ds-muted" style={{ fontFamily: 'Barlow, sans-serif' }}>
          Cómo te presenta el AI
        </p>
      </div>

      <Field label="Nombre o marca">
        <Input
          value={form.creator_name}
          onChange={v => onChange('creator_name', v)}
          placeholder="Tu nombre o marca..."
        />
      </Field>

      <Field label="Tipo de contenido">
        <div className="grid grid-cols-4 gap-2">
          {CREATOR_TYPES.map(type => (
            <button
              key={type.id}
              onClick={() => onChange('creator_type', type.id)}
              className={`flex flex-col items-center gap-1 p-2.5 border type-caption transition-all ${
                form.creator_type === type.id
                  ? 'bg-[rgba(212,43,30,0.08)] border-[rgba(212,43,30,0.35)] text-ds-text'
                  : 'bg-ds-elevated border-ds-border text-ds-muted hover:border-ds-border-mid'
              }`}
              style={{ borderRadius: 2 }}
            >
              <span className="text-base">{type.emoji}</span>
              <span className="leading-tight text-center">{type.label}</span>
            </button>
          ))}
        </div>
      </Field>

      <Field label="Nombre del proyecto">
        <Input
          value={form.project_name}
          onChange={v => onChange('project_name', v)}
          placeholder="Tu app, canal, marca..."
        />
      </Field>

      <Field label="Descripción del proyecto" hint="contexto para el AI">
        <Textarea
          value={form.project_description}
          onChange={v => onChange('project_description', v)}
          placeholder="¿De qué va tu proyecto? ¿Qué lo hace diferente?"
          rows={3}
        />
      </Field>
    </div>
  )
}

function TabAudiencia({ form, onChange }) {
  return (
    <div className="space-y-5">
      <div>
        <h2 className="type-h3 text-ds-text text-base mb-1">Audiencia y tono</h2>
        <p className="text-xs text-ds-muted" style={{ fontFamily: 'Barlow, sans-serif' }}>
          Para quién creas y cómo te expresas
        </p>
      </div>

      <Field label="¿Para quién creas contenido?">
        <Textarea
          value={form.target_audience}
          onChange={v => onChange('target_audience', v)}
          placeholder="Describe a tu audiencia: quiénes son, qué les preocupa, qué buscan..."
          rows={5}
        />
      </Field>

      <Field label="Tono de comunicación">
        <div className="grid grid-cols-3 gap-2">
          {TONES.map(tone => (
            <button
              key={tone.id}
              onClick={() => onChange('tone', tone.id)}
              className={`px-3 py-2 border type-caption transition-all ${
                form.tone === tone.id
                  ? 'bg-[rgba(212,43,30,0.08)] border-[rgba(212,43,30,0.35)] text-ds-text'
                  : 'bg-ds-elevated border-ds-border text-ds-muted hover:border-ds-border-mid'
              }`}
              style={{ borderRadius: 2 }}
            >
              {tone.label}
            </button>
          ))}
        </div>
      </Field>
    </div>
  )
}

function TabVoz({ form, onChange }) {
  return (
    <div className="space-y-5">
      <div>
        <h2 className="type-h3 text-ds-text text-base mb-1">Voz del creador</h2>
        <p className="text-xs text-ds-muted" style={{ fontFamily: 'Barlow, sans-serif' }}>
          Cuanto más detallado seas, más el AI suena exactamente como tú. Estos campos tienen
          prioridad sobre el estilo genérico.
        </p>
      </div>

      <Field label="Estilo narrativo" hint="cómo cuentas historias">
        <Textarea
          value={form.narrative_style}
          onChange={v => onChange('narrative_style', v)}
          placeholder="Ej: Parto siempre de algo real. No cuento historias cerradas, cuento el proceso mientras ocurre. Lo imperfecto es parte del estilo..."
          rows={3}
        />
      </Field>

      <Field label="Lo que nunca hago" hint="anti-patrones, qué evitas">
        <Textarea
          value={form.what_you_never_do}
          onChange={v => onChange('what_you_never_do', v)}
          placeholder="Ej: Nunca listas de consejos sin historia real. Nunca intros largas. Nunca copy que suena a plantilla..."
          rows={3}
        />
      </Field>

      <Field label="Reglas de escritura" hint="tu ritmo, cómo escribes">
        <Textarea
          value={form.writing_rules}
          onChange={v => onChange('writing_rules', v)}
          placeholder="Ej: Frases cortas. Punto. Cada frase se gana su sitio. La primera frase hace el vídeo inevitable..."
          rows={3}
        />
      </Field>

      <Field label="Frases de ejemplo" hint="fragmentos reales que suenan como tú">
        <Textarea
          value={form.example_phrases}
          onChange={v => onChange('example_phrases', v)}
          placeholder="Pega aquí frases o párrafos de guiones anteriores. El AI los usará como referencia de voz..."
          rows={4}
          mono
        />
      </Field>

      <Field label="Arco emocional" hint="cómo construyes la emoción">
        <Textarea
          value={form.emotional_approach}
          onChange={v => onChange('emotional_approach', v)}
          placeholder="Ej: Inicio con tensión contenida. El desarrollo es reconocimiento. El cierre deja peso emocional..."
          rows={3}
        />
      </Field>
    </div>
  )
}

function TabPlataformas({ form, onChange, togglePlatform }) {
  return (
    <div className="space-y-5">
      <div>
        <h2 className="type-h3 text-ds-text text-base mb-1">Plataformas</h2>
        <p className="text-xs text-ds-muted" style={{ fontFamily: 'Barlow, sans-serif' }}>
          Dónde publicas y cuál es tu plataforma principal
        </p>
      </div>

      <Field label="¿Dónde publicas?">
        <div className="grid grid-cols-2 gap-2">
          {PLATFORMS.map(platform => {
            const isSelected = (form.platforms || []).includes(platform.id)
            return (
              <button
                key={platform.id}
                onClick={() => togglePlatform(platform.id)}
                className={`flex items-center gap-3 p-3 border transition-all ${
                  isSelected
                    ? 'bg-[rgba(212,43,30,0.08)] border-[rgba(212,43,30,0.35)] text-ds-text'
                    : 'bg-ds-elevated border-ds-border text-ds-muted hover:border-ds-border-mid'
                }`}
                style={{ borderRadius: 2 }}
              >
                <span className="text-xl">{platform.emoji}</span>
                <span className="font-medium text-sm" style={{ fontFamily: 'Barlow, sans-serif' }}>
                  {platform.label}
                </span>
              </button>
            )
          })}
        </div>
      </Field>

      {(form.platforms || []).length > 1 && (
        <Field label="Plataforma principal">
          <div className="flex flex-wrap gap-2">
            {(form.platforms || []).map(platformId => {
              const platform = PLATFORMS.find(p => p.id === platformId)
              return (
                <button
                  key={platformId}
                  onClick={() => onChange('primary_platform', platformId)}
                  className={`flex items-center gap-2 px-3 py-1.5 border type-caption transition-all ${
                    form.primary_platform === platformId
                      ? 'bg-ds-red border-ds-red text-white'
                      : 'bg-ds-elevated border-ds-border text-ds-muted hover:border-ds-border-mid'
                  }`}
                  style={{ borderRadius: 2 }}
                >
                  <span>{platform?.emoji}</span>
                  <span>{platform?.label}</span>
                </button>
              )
            })}
          </div>
        </Field>
      )}
    </div>
  )
}

function TabApiKey() {
  const [settings, setSettings] = useState(null)
  const [provider, setProvider] = useState('anthropic')
  const [apiKey, setApiKey] = useState('')
  const [saving, setSaving] = useState(false)
  const [removing, setRemoving] = useState(false)
  const [showKey, setShowKey] = useState(false)

  useEffect(() => {
    api
      .get('/user-settings')
      .then(res => setSettings(res.data))
      .catch(() => {})
  }, [])

  const handleSave = async () => {
    if (!apiKey.trim()) return
    setSaving(true)
    try {
      await api.put('/user-settings/byok', { provider, api_key: apiKey })
      setSettings(prev => ({
        ...prev,
        has_byok_key: true,
        byok_provider: provider,
      }))
      setApiKey('')
      toast.success('API key guardada. Ahora tienes generaciones ilimitadas.')
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error al guardar la API key')
    } finally {
      setSaving(false)
    }
  }

  const handleRemove = async () => {
    setRemoving(true)
    try {
      await api.delete('/user-settings/byok')
      setSettings(prev => ({ ...prev, has_byok_key: false, byok_provider: null }))
      toast.success('API key eliminada')
    } catch {
      toast.error('Error al eliminar la API key')
    } finally {
      setRemoving(false)
    }
  }

  const providerInfo = BYOK_PROVIDERS.find(p => p.id === (settings?.byok_provider || provider))

  return (
    <div className="space-y-5">
      <div>
        <h2 className="type-h3 text-ds-text text-base mb-1">API Key propia</h2>
        <p className="text-xs text-[#6E6E73] leading-relaxed">
          Conecta tu propia API key para tener{' '}
          <span className="text-white">generaciones ilimitadas</span>. Compatible con Anthropic,
          OpenAI, DeepSeek, Groq y Gemini. Tu key se almacena cifrada y nunca se expone.
        </p>
      </div>

      {settings?.has_byok_key ? (
        <div className="flex items-center justify-between card-elevated px-4 py-3">
          <div className="flex items-center gap-2">
            <Lightning size={14} color="#30D158" weight="fill" />
            <span
              className="text-ds-text text-sm font-medium"
              style={{ fontFamily: 'Barlow, sans-serif' }}
            >
              {BYOK_PROVIDERS.find(p => p.id === settings.byok_provider)?.label ||
                settings.byok_provider}
            </span>
            <span className="type-caption text-ds-faint">activa</span>
          </div>
          <button
            onClick={handleRemove}
            disabled={removing}
            className="flex items-center gap-1.5 text-xs text-ds-muted hover:text-ds-red transition-colors"
            style={{ fontFamily: 'Barlow, sans-serif' }}
          >
            <Trash size={13} />
            Eliminar
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          <div>
            <label className="ds-label">Proveedor</label>
            <select
              value={provider}
              onChange={e => setProvider(e.target.value)}
              className="ds-select"
            >
              {BYOK_PROVIDERS.map(p => (
                <option key={p.id} value={p.id}>
                  {p.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="ds-label">API Key</label>
            <div className="relative">
              <input
                type={showKey ? 'text' : 'password'}
                value={apiKey}
                onChange={e => setApiKey(e.target.value)}
                placeholder={providerInfo?.placeholder || 'Tu API key...'}
                className="ds-input pr-16 font-mono"
              />
              <button
                type="button"
                onClick={() => setShowKey(v => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 type-caption text-ds-muted hover:text-ds-text transition-colors"
              >
                {showKey ? 'Ocultar' : 'Ver'}
              </button>
            </div>
          </div>
          <button
            onClick={handleSave}
            disabled={!apiKey.trim() || saving}
            className="w-full flex items-center justify-center gap-2 bg-[rgba(212,43,30,0.08)] hover:bg-[rgba(212,43,30,0.14)] border border-[rgba(212,43,30,0.2)] text-ds-red type-btn text-[12px] py-2.5 transition-all disabled:opacity-40"
            style={{ borderRadius: 2 }}
          >
            {saving ? (
              <div className="w-4 h-4 border-2 border-[rgba(212,43,30,0.3)] border-t-ds-red rounded-full animate-spin" />
            ) : (
              <Key size={14} />
            )}
            Guardar API key
          </button>
        </div>
      )}
    </div>
  )
}

/* ── Producción tab ─────────────────────────────────────────────────── */

function TabProduccion({ form, onChange }) {
  const config = form.production_config || {
    equipment_level: 'basic',
    layers: { cinematography: true, sound_design: false, broll: true },
  }
  const setConfig = updater => {
    const next = typeof updater === 'function' ? updater(config) : updater
    onChange('production_config', next)
  }

  return (
    <div className="space-y-6">
      <div className="space-y-1.5">
        <label className="ds-label mb-0">Nivel de equipo</label>
        <p className="text-ds-faint text-xs" style={{ fontFamily: 'Barlow, sans-serif' }}>
          Define la profundidad de las instrucciones de cinematografía generadas en cada sección.
        </p>
        <div className="grid grid-cols-2 gap-2 mt-2">
          {EQUIPMENT_LEVELS.map(lvl => (
            <button
              key={lvl.id}
              onClick={() => setConfig(c => ({ ...c, equipment_level: lvl.id }))}
              className={`flex flex-col items-start px-3 py-2.5 border text-left transition-all ${
                config.equipment_level === lvl.id
                  ? 'bg-[rgba(212,43,30,0.08)] border-[rgba(212,43,30,0.35)] text-ds-text'
                  : 'bg-ds-elevated border-ds-border text-ds-muted hover:border-ds-border-mid'
              }`}
              style={{ borderRadius: 2 }}
            >
              <span className="text-xs font-semibold">{lvl.label}</span>
              <span
                className="text-[10px] text-ds-faint mt-0.5 leading-snug"
                style={{ fontFamily: 'Barlow, sans-serif' }}
              >
                {lvl.desc}
              </span>
            </button>
          ))}
        </div>
      </div>

      <div className="space-y-1.5">
        <label className="ds-label mb-0">Capas de producción</label>
        <p className="text-ds-faint text-xs" style={{ fontFamily: 'Barlow, sans-serif' }}>
          Activa qué módulos se generan en cada guión.
        </p>
        <div className="space-y-2 mt-2">
          {[
            {
              key: 'cinematography',
              label: 'Cinematografía',
              desc: 'Planos, movimientos de cámara, encuadre y luz por sección',
            },
            {
              key: 'sound_design',
              label: 'Diseño sonoro',
              desc: 'Brief musical en capas para todo el vídeo (consume créditos extra)',
            },
            {
              key: 'broll',
              label: 'B-Roll',
              desc: 'Escenas visuales Remotion generadas por IA',
            },
          ].map(layer => (
            <button
              key={layer.key}
              onClick={() =>
                setConfig(c => ({
                  ...c,
                  layers: { ...c.layers, [layer.key]: !c.layers?.[layer.key] },
                }))
              }
              className={`w-full flex items-center gap-3 px-3 py-2.5 border text-left transition-all ${
                config.layers?.[layer.key]
                  ? 'bg-[rgba(212,43,30,0.08)] border-[rgba(212,43,30,0.35)]'
                  : 'bg-ds-elevated border-ds-border hover:border-ds-border-mid'
              }`}
              style={{ borderRadius: 2 }}
            >
              <div
                className={`shrink-0 w-4 h-4 border flex items-center justify-center ${
                  config.layers?.[layer.key]
                    ? 'bg-[#D42B1E] border-[#D42B1E]'
                    : 'border-ds-border-mid'
                }`}
                style={{ borderRadius: 2 }}
              >
                {config.layers?.[layer.key] && (
                  <svg width="8" height="8" viewBox="0 0 8 8" fill="none">
                    <path
                      d="M1 4l2 2 4-4"
                      stroke="white"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                )}
              </div>
              <div>
                <span
                  className={`text-xs font-semibold ${config.layers?.[layer.key] ? 'text-ds-text' : 'text-ds-muted'}`}
                >
                  {layer.label}
                </span>
                <span
                  className="block text-[10px] text-ds-faint mt-0.5"
                  style={{ fontFamily: 'Barlow, sans-serif' }}
                >
                  {layer.desc}
                </span>
              </div>
            </button>
          ))}
        </div>
      </div>
    </div>
  )
}

/* ── Main component ─────────────────────────────────────────────────── */

export default function ProfilePage() {
  const { user, logout, refreshProfile } = useAuth()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  // La barra de progreso enlaza a la pestaña donde vive la pregunta que falta.
  const [activeTab, setActiveTab] = useState(() => {
    const requested = searchParams.get('tab')
    return TABS.some(t => t.id === requested) ? requested : 'cuenta'
  })
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [settings, setSettings] = useState(null)
  const [form, setForm] = useState({
    creator_name: user?.name || '',
    creator_type: '',
    project_name: '',
    project_description: '',
    target_audience: '',
    tone: '',
    platforms: [],
    primary_platform: '',
    narrative_style: '',
    what_you_never_do: '',
    writing_rules: '',
    example_phrases: '',
    emotional_approach: '',
    production_config: {
      equipment_level: 'basic',
      layers: { cinematography: true, sound_design: false, broll: true },
    },
  })

  useEffect(() => {
    Promise.all([
      api.get('/profile').catch(() => ({ data: null })),
      api.get('/user-settings').catch(() => ({ data: null })),
    ])
      .then(([profileRes, settingsRes]) => {
        if (profileRes.data) {
          const { completeness: _serverOnly, ...profile } = profileRes.data
          setForm(prev => ({ ...prev, ...profile }))
        }
        if (settingsRes.data) setSettings(settingsRes.data)
      })
      .finally(() => setLoading(false))
  }, [])

  const onChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }))
  }

  const togglePlatform = platformId => {
    const current = form.platforms || []
    const updated = current.includes(platformId)
      ? current.filter(p => p !== platformId)
      : [...current, platformId]
    onChange('platforms', updated)
    if (form.primary_platform === platformId && !updated.includes(platformId)) {
      onChange('primary_platform', updated[0] || '')
    }
    if (updated.length === 1) onChange('primary_platform', updated[0])
  }

  const handleSave = async () => {
    if (!form.creator_name?.trim()) {
      toast.error('El nombre es obligatorio: es con lo que la IA te habla')
      return
    }
    setSaving(true)
    try {
      await api.put('/profile', form)
      refreshProfile()
      toast.success('Perfil actualizado')
    } catch {
      toast.error('Error al guardar el perfil')
    } finally {
      setSaving(false)
    }
  }

  const handleLogout = () => {
    logout()
    navigate('/auth')
  }

  const showSave = !['cuenta', 'apikey'].includes(activeTab)

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-ds-red border-t-transparent rounded-full animate-spin" />
      </div>
    )
  }

  return (
    <div className="flex h-full overflow-hidden">
      {/* Left tab rail */}
      <nav className="w-48 shrink-0 border-r border-ds-border flex flex-col py-6 px-3 gap-0.5">
        {TABS.map(tab => {
          const Icon = tab.icon
          const isActive = activeTab === tab.id
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`nav-item w-full text-left ${isActive ? 'active' : ''}`}
            >
              <Icon size={15} weight={isActive ? 'fill' : 'regular'} />
              {tab.label}
            </button>
          )
        })}
      </nav>

      {/* Content area */}
      <div className="flex-1 flex flex-col overflow-hidden">
        <div className="flex-1 overflow-y-auto px-8 py-7">
          <div className="max-w-lg">
            {activeTab === 'cuenta' && (
              <TabCuenta user={user} onLogout={handleLogout} settings={settings} />
            )}
            {activeTab === 'identidad' && <TabIdentidad form={form} onChange={onChange} />}
            {activeTab === 'audiencia' && <TabAudiencia form={form} onChange={onChange} />}
            {activeTab === 'voz' && <TabVoz form={form} onChange={onChange} />}
            {activeTab === 'plataformas' && (
              <TabPlataformas form={form} onChange={onChange} togglePlatform={togglePlatform} />
            )}
            {activeTab === 'produccion' && <TabProduccion form={form} onChange={onChange} />}
            {activeTab === 'apikey' && <TabApiKey />}
          </div>
        </div>

        {/* Save bar */}
        {showSave && (
          <div className="flex-shrink-0 border-t border-ds-border px-8 py-4 flex justify-end">
            <button
              onClick={handleSave}
              disabled={saving}
              className="btn-accent flex items-center gap-2 disabled:opacity-50"
            >
              {saving ? (
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              ) : (
                <FloppyDisk size={15} weight="bold" />
              )}
              Guardar cambios
            </button>
          </div>
        )}
      </div>
    </div>
  )
}
